/**
 * QAController — singleton debug controller for Rush Fever 7s Deluxe.
 *
 * Activated via URL param ?qa=1.  Safe to import when QA mode is off — all
 * public methods gracefully no-op or return defaults.
 *
 * Keyboard shortcut: Ctrl+Shift+Q toggles the QA panel visibility.
 * Exposed as window.__qa__ when enabled for console-driven testing.
 */

import { GameConfig } from '../config/GameConfig';
import type { QAOverride, QASpinResult, QAScenarioId } from './QAOverrides';
import { getScenario, QA_SCENARIOS } from './QAScenarios';

// ── Logging helper ──────────────────────────────────────────────────────────

const LOG_KEY = 'qa_controller_log';

function qaLog(msg: string): void {
  const ts = new Date().toISOString();
  const line = `[${ts}] QA: ${msg}`;
  // eslint-disable-next-line no-console
  console.log(line);
  try {
    const prev = sessionStorage.getItem(LOG_KEY) ?? '';
    sessionStorage.setItem(LOG_KEY, prev + line + '\n');
  } catch { /* quota exceeded — ignore */ }
}

// ── Default override state ──────────────────────────────────────────────────

function defaultOverride(): QAOverride {
  return {
    forceFeature: null,
    rushCount: undefined,
    bigWinTier: undefined,
    jpPicks: undefined,
    forceGrid: undefined,
    forceBalance: undefined,
    forceBetIndex: undefined,
    speedMultiplier: 1,
    skipAnimations: false,
    sticky: false,
    active: false,
  };
}

// ── Extend Window ───────────────────────────────────────────────────────────

declare global {
  interface Window {
    __qa__?: QAController;
  }
}

// ── Controller ──────────────────────────────────────────────────────────────

export class QAController {
  private static _instance: QAController | null = null;

  private _override: QAOverride;
  private _spinHistory: QASpinResult[] = [];
  private _spinCallbacks: Array<(r: QASpinResult) => void> = [];
  private _toggleCallbacks: Array<(visible: boolean) => void> = [];
  private _panelVisible = false;

  // ── Singleton ───────────────────────────────────────────────────

  static getInstance(): QAController {
    if (!QAController._instance) {
      QAController._instance = new QAController();
    }
    return QAController._instance;
  }

  /** Check if QA mode is enabled (via ?qa=1) without creating the singleton */
  static isEnabled(): boolean {
    try {
      return new URLSearchParams(window.location.search).get('qa') === '1';
    } catch {
      return false;
    }
  }

  private constructor() {
    this._override = defaultOverride();
    if (QAController.isEnabled()) {
      this._override.active = true;
      this.parseURLParams();
      this._bindKeyboard();
      window.__qa__ = this;
      qaLog('QA mode enabled');
    }
  }

  // ── URL parsing ─────────────────────────────────────────────────

  parseURLParams(): void {
    const params = new URLSearchParams(window.location.search);

    const force = params.get('force') as QAOverride['forceFeature'] | null;
    if (force) {
      this._override.forceFeature = force;
      qaLog(`URL: forceFeature=${force}`);
    }

    const balance = params.get('balance');
    if (balance !== null) {
      const val = parseFloat(balance);
      if (!isNaN(val)) {
        this._override.forceBalance = val;
        qaLog(`URL: forceBalance=${val}`);
      }
    }

    const bet = params.get('bet');
    if (bet !== null) {
      const val = parseFloat(bet);
      if (!isNaN(val)) {
        // Find closest bet-level index
        const BET_LEVELS = GameConfig.BET_LEVELS;
        let bestIdx = 0;
        let bestDist = Infinity;
        for (let i = 0; i < BET_LEVELS.length; i++) {
          const d = Math.abs(BET_LEVELS[i] - val);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        }
        this._override.forceBetIndex = bestIdx;
        qaLog(`URL: forceBetIndex=${bestIdx} (requested ${val})`);
      }
    }

    const speed = params.get('speed');
    if (speed !== null) {
      const val = parseFloat(speed);
      if (!isNaN(val) && val > 0) {
        this._override.speedMultiplier = val;
        qaLog(`URL: speedMultiplier=${val}`);
      }
    }

    const scenario = params.get('scenario') as QAScenarioId | null;
    if (scenario && getScenario(scenario)) {
      this.applyScenario(scenario);
    }
  }

  // ── Override accessors ──────────────────────────────────────────

  get override(): Readonly<QAOverride> {
    return this._override;
  }

  setOverride(partial: Partial<QAOverride>): void {
    Object.assign(this._override, partial);
    qaLog(`setOverride: ${JSON.stringify(partial)}`);
  }

  clearOverrides(): void {
    const wasActive = this._override.active;
    this._override = defaultOverride();
    this._override.active = wasActive;
    qaLog('overrides cleared');
  }

  applyScenario(id: QAScenarioId): void {
    const scenario = getScenario(id);
    if (!scenario) {
      qaLog(`unknown scenario: ${id}`);
      return;
    }
    this.clearOverrides();
    Object.assign(this._override, scenario.override);
    qaLog(`scenario applied: ${id}`);
  }

  // ── Grid overrides ─────────────────────────────────────────────

  hasGridOverride(): boolean {
    return this._override.active && this._override.forceGrid != null;
  }

  /** Returns the grid override and clears it (unless sticky) */
  consumeGridOverride(): (string | null)[][] | undefined {
    const grid = this._override.forceGrid;
    if (grid && !this._override.sticky) {
      this._override.forceGrid = undefined;
      qaLog('grid override consumed');
    }
    return grid;
  }

  // ── Feature overrides ──────────────────────────────────────────

  shouldForceFeature(feature: string): boolean {
    return this._override.active && this._override.forceFeature === feature;
  }

  /** Returns and clears the forced feature (unless sticky) */
  consumeFeatureOverride(): QAOverride['forceFeature'] {
    const f = this._override.forceFeature;
    if (f && !this._override.sticky) {
      this._override.forceFeature = null;
      qaLog(`feature override consumed: ${f}`);
    }
    return f;
  }

  // ── Speed / balance / bet ──────────────────────────────────────

  getSpeedMultiplier(): number {
    return this._override.active ? this._override.speedMultiplier : 1;
  }

  getForceBalance(): number | undefined {
    return this._override.active ? this._override.forceBalance : undefined;
  }

  getForceBet(): number | undefined {
    return this._override.active ? this._override.forceBetIndex : undefined;
  }

  // ── Spin recording ─────────────────────────────────────────────

  onSpin(callback: (r: QASpinResult) => void): void {
    this._spinCallbacks.push(callback);
  }

  recordSpin(result: QASpinResult): void {
    this._spinHistory.push(result);
    qaLog(`spin: bet=${result.totalBet} win=${result.totalWin} features=[${result.features.join(',')}]`);
    for (const cb of this._spinCallbacks) {
      try { cb(result); } catch (e) { qaLog(`spin callback error: ${e}`); }
    }
  }

  get spinHistory(): readonly QASpinResult[] {
    return this._spinHistory;
  }

  // ── Panel toggle ───────────────────────────────────────────────

  onToggle(callback: (visible: boolean) => void): void {
    this._toggleCallbacks.push(callback);
  }

  toggle(): void {
    this._panelVisible = !this._panelVisible;
    qaLog(`panel toggled: ${this._panelVisible ? 'visible' : 'hidden'}`);
    for (const cb of this._toggleCallbacks) {
      try { cb(this._panelVisible); } catch (e) { qaLog(`toggle callback error: ${e}`); }
    }
  }

  get panelVisible(): boolean {
    return this._panelVisible;
  }

  // ── Keyboard shortcut ──────────────────────────────────────────

  private _bindKeyboard(): void {
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'Q') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  // ── Utility ────────────────────────────────────────────────────

  /** Dump current state to console (useful from devtools) */
  dump(): void {
    // eslint-disable-next-line no-console
    console.table({
      active: this._override.active,
      forceFeature: this._override.forceFeature ?? '(none)',
      rushCount: this._override.rushCount ?? '(none)',
      bigWinTier: this._override.bigWinTier ?? '(none)',
      jpPicks: this._override.jpPicks?.join(',') ?? '(none)',
      hasGrid: this._override.forceGrid != null,
      forceBalance: this._override.forceBalance ?? '(none)',
      forceBetIndex: this._override.forceBetIndex ?? '(none)',
      speedMultiplier: this._override.speedMultiplier,
      skipAnimations: this._override.skipAnimations,
      sticky: this._override.sticky,
      spinsRecorded: this._spinHistory.length,
      panelVisible: this._panelVisible,
    });
  }

  /** Available scenario IDs */
  get scenarioIds(): QAScenarioId[] {
    return [...QA_SCENARIOS.keys()];
  }

  /** Get logs from session storage */
  getLogs(): string {
    try {
      return sessionStorage.getItem(LOG_KEY) ?? '';
    } catch {
      return '';
    }
  }
}
